import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { http } from "@/lib/api";
import { LogOut, Users, Handshake, MessageSquare, HelpCircle, FileText, Quote, Plus, Trash2, HeartPulse, Building2, Stethoscope, Globe, ChevronUp, ChevronDown, Eye, EyeOff, Edit2, Save } from "lucide-react";
import RichEditor from "@/components/RichEditor";

export function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(!!localStorage.getItem("mp_token"));
  const submit = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      localStorage.setItem("mp_token", data.token);
      setOk(true);
    } catch (e) { setErr(e.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };
  if (ok) return <Navigate to="/admin" replace/>;
  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6">
      <div className="mp-card p-8 w-full max-w-md">
        <div className="overline mb-3">Admin Portal</div>
        <h1 className="font-serif text-3xl text-[#F3E9EC]">Sign in</h1>
        <div className="mt-6 space-y-4">
          <input className="mp-input" placeholder="Email" value={email} onChange={e=>setEmail(e.target.value)} data-testid="admin-login-email"/>
          <input className="mp-input" type="password" placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} data-testid="admin-login-password"/>
          {err && <p className="text-red-400 text-sm">{err}</p>}
          <button onClick={submit} disabled={loading} className="btn-primary px-6 py-3 w-full disabled:opacity-50" data-testid="admin-login-submit">{loading?"Signing in…":"Sign in"}</button>
        </div>
      </div>
    </div>
  );
}

export function Admin() {
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [partners, setPartners] = useState([]);
  const [convs, setConvs] = useState([]);
  const [unans, setUnans] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [tests, setTests] = useState([]);
  const [me, setMe] = useState(null);
  const [authState, setAuthState] = useState("loading"); // loading | ok | failed
  useEffect(() => {
    const token = localStorage.getItem("mp_token");
    if (!token) { setAuthState("failed"); return; }
    http.get("/auth/me")
      .then(r => { setMe(r.data); setAuthState("ok"); })
      .catch(() => { localStorage.removeItem("mp_token"); setAuthState("failed"); });
  }, []);
  useEffect(() => {
    if (!me) return;
    if (tab === "leads") http.get("/admin/leads").then(r => setLeads(r.data));
    if (tab === "partners") http.get("/admin/partners").then(r => setPartners(r.data));
    if (tab === "conversations") http.get("/admin/conversations").then(r => setConvs(r.data));
    if (tab === "unanswered") http.get("/admin/unanswered").then(r => setUnans(r.data));
    if (tab === "blog") http.get("/admin/blog").then(r => setBlogs(r.data));
    if (tab === "testimonials") http.get("/admin/testimonials").then(r => setTests(r.data));
  }, [tab, me]);
  if (authState === "loading") return <div className="max-w-7xl mx-auto px-6 py-24 text-[#F3E9EC]/60" data-testid="admin-loading">Loading admin…</div>;
  if (authState === "failed" || !me) return <Navigate to="/admin/login" replace/>;
  const logout = () => { localStorage.removeItem("mp_token"); window.location.href = "/admin/login"; };
  const updateStatus = async (id, status) => {
    await http.patch(`/admin/leads/${id}`, { status });
    http.get("/admin/leads").then(r => setLeads(r.data));
  };
  const STATUSES = ["New","Contacted","Documents Requested","Under Review","Quote Sent","Hospital Consultation","Travel Planning","Treatment Completed","Follow-Up","Closed"];
  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex justify-between items-center mb-10">
        <div>
          <div className="overline mb-2">MediPats Admin</div>
          <h1 className="font-serif text-3xl md:text-4xl text-[#F3E9EC]">Care Coordinator Dashboard</h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-[#F3E9EC]/60">{me.email}</span>
          <button onClick={logout} className="btn-outline-mauve px-4 py-2 flex items-center gap-2 text-sm" data-testid="admin-logout"><LogOut className="w-4 h-4"/>Sign out</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-8">
        {[["leads","Enquiries",Users],["partners","Partners",Handshake],["conversations","Chat",MessageSquare],["unanswered","Unanswered",HelpCircle],["blog","Blog",FileText],["testimonials","Testimonials",Quote],["treatments","Treatments",HeartPulse],["hospitals","Hospitals",Building2],["doctors","Doctors",Stethoscope],["countries","Countries",Globe]].map(([k,l,Icon]) => (
          <button key={k} onClick={()=>setTab(k)} className={`px-4 py-2 rounded-full text-sm flex items-center gap-2 border transition-colors ${tab===k?"bg-[#B47A9A] border-[#B47A9A] text-[#1A0F1B]":"border-[#5E3A5C]/40 text-[#F3E9EC]/70 hover:text-white hover:border-[#B47A9A]/50"}`} data-testid={`admin-tab-${k}`}>
            <Icon className="w-4 h-4"/>{l}
          </button>
        ))}
      </div>

      {tab === "leads" && (
        <div className="mp-card overflow-hidden" data-testid="admin-leads-table">
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-[#5E3A5C]/25"><tr>
              {["Enquiry ID","Patient","Country","Treatment","Status","Date","Actions"].map(h=><th key={h} className="text-left px-4 py-3 text-[#F3E9EC]/75 font-medium text-xs uppercase tracking-wider">{h}</th>)}
            </tr></thead>
            <tbody>{leads.map(l => (
              <tr key={l.id} className="border-t border-[#5E3A5C]/25">
                <td className="px-4 py-3 font-mono text-[#B47A9A]">{l.enquiry_id}</td>
                <td className="px-4 py-3 text-[#F3E9EC]">{l.full_name}</td>
                <td className="px-4 py-3 text-[#F3E9EC]/75">{l.country}</td>
                <td className="px-4 py-3 text-[#F3E9EC]/75">{l.treatment || "—"}</td>
                <td className="px-4 py-3"><select value={l.status} onChange={e=>updateStatus(l.id, e.target.value)} className="mp-select text-xs py-1.5">{STATUSES.map(s=><option key={s}>{s}</option>)}</select></td>
                <td className="px-4 py-3 text-[#F3E9EC]/60 text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-xs text-[#B47A9A]">{l.source||"website"}</td>
              </tr>
            ))}</tbody></table></div>
          {leads.length === 0 && <p className="px-6 py-8 text-[#F3E9EC]/50">No enquiries yet.</p>}
        </div>
      )}

      {tab === "partners" && (
        <div className="grid md:grid-cols-2 gap-4">{partners.map(p=>(
          <div key={p.id} className="mp-card p-6">
            <div className="flex justify-between items-start"><div><h3 className="font-serif text-xl text-[#F3E9EC]">{p.org_name}</h3><p className="text-xs text-[#B47A9A] mt-1 uppercase tracking-widest">{p.type} · {p.specialty}</p></div><span className="text-xs px-2 py-1 rounded-full bg-[#B47A9A]/15 text-[#B47A9A]">{p.status}</span></div>
            <div className="mt-3 text-sm text-[#F3E9EC]/70 space-y-1"><p>{p.email} · {p.phone}</p><p>{p.city}</p><p className="text-xs mt-2">{p.message}</p></div>
          </div>
        ))}{partners.length===0 && <p className="text-[#F3E9EC]/50">No partner applications yet.</p>}</div>
      )}

      {tab === "conversations" && (
        <div className="space-y-3">{convs.map(c=>(
          <details key={c.session_id} className="mp-card p-5">
            <summary className="cursor-pointer flex justify-between items-center">
              <div><span className="font-mono text-xs text-[#B47A9A]">{c.session_id.slice(0,20)}…</span><p className="text-sm text-[#F3E9EC]/80 mt-1">{c.message_count} messages · {c.lead_id ? "Converted to lead" : "No conversion"}</p></div>
              <span className="text-xs text-[#F3E9EC]/50">{new Date(c.last_message_at||c.created_at).toLocaleString()}</span>
            </summary>
            <ConvDetail sessionId={c.session_id}/>
          </details>
        ))}{convs.length===0 && <p className="text-[#F3E9EC]/50">No conversations yet.</p>}</div>
      )}

      {tab === "unanswered" && (
        <div className="space-y-3">{unans.map(u=>(
          <div key={u.id} className="mp-card p-5">
            <p className="text-[#F3E9EC]">{u.question}</p>
            <p className="text-xs text-[#F3E9EC]/50 mt-2">{new Date(u.created_at).toLocaleString()}</p>
          </div>
        ))}{unans.length===0 && <p className="text-[#F3E9EC]/50">No unanswered questions.</p>}</div>
      )}

      {tab === "blog" && <BlogAdmin blogs={blogs} setBlogs={setBlogs}/>}
      {tab === "testimonials" && <TestimonialAdmin tests={tests} setTests={setTests}/>}
      {tab === "treatments" && <EntityAdmin key="t" endpoint="treatments" fields={TREATMENT_FIELDS} label="Treatment"/>}
      {tab === "hospitals" && <EntityAdmin key="h" endpoint="hospitals" fields={HOSPITAL_FIELDS} label="Hospital"/>}
      {tab === "doctors" && <EntityAdmin key="d" endpoint="doctors" fields={DOCTOR_FIELDS} label="Doctor"/>}
      {tab === "countries" && <CountryAdmin/>}
    </div>
  );
}

function BlogAdmin({ blogs, setBlogs }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ slug:"", title:"", excerpt:"", category:"", tags:"", featured_image:"", seo_title:"", meta_description:"", content:"", published:true });
  const refresh = () => http.get("/admin/blog").then(r => setBlogs(r.data));
  const submit = async () => {
    try {
      const payload = { ...form, tags: form.tags.split(",").map(s=>s.trim()).filter(Boolean) };
      await http.post("/admin/blog", payload);
      setCreating(false);
      setForm({ slug:"", title:"", excerpt:"", category:"", tags:"", featured_image:"", seo_title:"", meta_description:"", content:"", published:true });
      refresh();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };
  const del = async (slug) => {
    if (!window.confirm(`Delete "${slug}"?`)) return;
    await http.delete(`/admin/blog/${slug}`);
    refresh();
  };
  const toggle = async (b) => {
    await http.patch(`/admin/blog/${b.slug}`, { published: !b.published });
    refresh();
  };
  return (
    <div data-testid="admin-blog-panel">
      <div className="mb-4 flex justify-end">
        <button onClick={()=>setCreating(!creating)} className="btn-primary px-4 py-2 flex items-center gap-2 text-sm" data-testid="admin-new-blog"><Plus className="w-4 h-4"/>{creating?"Cancel":"New article"}</button>
      </div>
      {creating && (
        <div className="mp-card p-6 mb-6 space-y-3">
          <input className="mp-input" placeholder="Slug (e.g. medical-visa-india-guide)" value={form.slug} onChange={e=>setForm({...form,slug:e.target.value})} data-testid="blog-form-slug"/>
          <input className="mp-input" placeholder="Title" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} data-testid="blog-form-title"/>
          <input className="mp-input" placeholder="Excerpt" value={form.excerpt} onChange={e=>setForm({...form,excerpt:e.target.value})}/>
          <div className="grid md:grid-cols-2 gap-3">
            <input className="mp-input" placeholder="Category" value={form.category} onChange={e=>setForm({...form,category:e.target.value})}/>
            <input className="mp-input" placeholder="Tags (comma-separated)" value={form.tags} onChange={e=>setForm({...form,tags:e.target.value})}/>
          </div>
          <input className="mp-input" placeholder="Featured image URL" value={form.featured_image} onChange={e=>setForm({...form,featured_image:e.target.value})}/>
          <input className="mp-input" placeholder="SEO title" value={form.seo_title} onChange={e=>setForm({...form,seo_title:e.target.value})}/>
          <textarea className="mp-textarea min-h-[80px]" placeholder="Meta description (150-160 chars)" value={form.meta_description} onChange={e=>setForm({...form,meta_description:e.target.value})}/>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#F3E9EC]/60 mb-2 block">Article content</label>
            <RichEditor value={form.content} onChange={(html)=>setForm(f=>({...f,content:html}))}/>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-[#F3E9EC]/75"><input type="checkbox" checked={form.published} onChange={e=>setForm({...form,published:e.target.checked})} className="accent-[#B47A9A]"/>Publish immediately</label>
            <button onClick={submit} disabled={!form.slug || !form.title || !form.content} className="btn-primary px-6 py-2 disabled:opacity-50 ml-auto" data-testid="blog-form-submit">Save article</button>
          </div>
        </div>
      )}
      <div className="space-y-3">
        {blogs.map(b => (
          <div key={b.id} className="mp-card p-5 flex items-start justify-between gap-4" data-testid={`admin-blog-row-${b.slug}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-serif text-lg text-[#F3E9EC] truncate">{b.title}</h3>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${b.published?"bg-[#25D366]/15 text-[#25D366]":"bg-[#B47A9A]/15 text-[#B47A9A]"}`}>{b.published?"Live":"Draft"}</span>
              </div>
              <p className="text-xs text-[#F3E9EC]/50 mt-1 font-mono">/blog/{b.slug} · {b.category} · {(b.tags||[]).join(", ")}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={()=>toggle(b)} className="btn-outline-mauve px-3 py-1.5 text-xs" data-testid={`admin-blog-toggle-${b.slug}`}>{b.published?"Unpublish":"Publish"}</button>
              <button onClick={()=>del(b.slug)} className="btn-outline-mauve px-3 py-1.5 text-xs text-red-300" data-testid={`admin-blog-delete-${b.slug}`}><Trash2 className="w-3.5 h-3.5"/></button>
            </div>
          </div>
        ))}
        {blogs.length===0 && <p className="text-[#F3E9EC]/50">No articles yet — click "New article" to create one.</p>}
      </div>
    </div>
  );
}

function TestimonialAdmin({ tests, setTests }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ patient_display_name:"", country:"", country_slug:"", treatment_slug:"", treatment_name:"", quote:"", rating:5, consent:false, published:true });
  const refresh = () => http.get("/admin/testimonials").then(r => setTests(r.data));
  const submit = async () => {
    if (!form.consent) return alert("Consent must be captured before publishing a patient testimonial.");
    try {
      await http.post("/admin/testimonials", form);
      setCreating(false);
      setForm({ patient_display_name:"", country:"", country_slug:"", treatment_slug:"", treatment_name:"", quote:"", rating:5, consent:false, published:true });
      refresh();
    } catch (e) { alert("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await http.delete(`/admin/testimonials/${id}`);
    refresh();
  };
  return (
    <div data-testid="admin-testimonials-panel">
      <div className="mb-4 flex justify-end">
        <button onClick={()=>setCreating(!creating)} className="btn-primary px-4 py-2 flex items-center gap-2 text-sm" data-testid="admin-new-testimonial"><Plus className="w-4 h-4"/>{creating?"Cancel":"New testimonial"}</button>
      </div>
      {creating && (
        <div className="mp-card p-6 mb-6 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <input className="mp-input" placeholder="Patient display name (e.g. James R.)" value={form.patient_display_name} onChange={e=>setForm({...form,patient_display_name:e.target.value})} data-testid="testimonial-form-name"/>
            <input className="mp-input" placeholder="Country (e.g. United Kingdom)" value={form.country} onChange={e=>setForm({...form,country:e.target.value})}/>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <input className="mp-input" placeholder="Country slug (e.g. uk)" value={form.country_slug} onChange={e=>setForm({...form,country_slug:e.target.value})}/>
            <input className="mp-input" placeholder="Treatment slug (optional)" value={form.treatment_slug} onChange={e=>setForm({...form,treatment_slug:e.target.value})}/>
          </div>
          <input className="mp-input" placeholder="Treatment display name" value={form.treatment_name} onChange={e=>setForm({...form,treatment_name:e.target.value})}/>
          <textarea className="mp-textarea min-h-[120px]" placeholder="Testimonial quote" value={form.quote} onChange={e=>setForm({...form,quote:e.target.value})} data-testid="testimonial-form-quote"/>
          <div className="flex items-center gap-4">
            <label className="text-sm text-[#F3E9EC]/75">Rating: <select value={form.rating} onChange={e=>setForm({...form,rating:Number(e.target.value)})} className="mp-select inline-block w-20 py-1 text-sm ml-2">{[5,4,3,2,1].map(n=><option key={n}>{n}</option>)}</select></label>
            <label className="flex items-center gap-2 text-sm text-[#F3E9EC]/75"><input type="checkbox" checked={form.consent} onChange={e=>setForm({...form,consent:e.target.checked})} className="accent-[#B47A9A]" data-testid="testimonial-form-consent"/>Consent captured</label>
            <label className="flex items-center gap-2 text-sm text-[#F3E9EC]/75"><input type="checkbox" checked={form.published} onChange={e=>setForm({...form,published:e.target.checked})} className="accent-[#B47A9A]"/>Publish</label>
            <button onClick={submit} disabled={!form.patient_display_name || !form.quote} className="btn-primary px-6 py-2 disabled:opacity-50 ml-auto" data-testid="testimonial-form-submit">Save</button>
          </div>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {tests.map(t => (
          <div key={t.id} className="mp-card p-5 flex flex-col" data-testid={`admin-testimonial-${t.id}`}>
            <div className="flex justify-between items-start">
              <div><h3 className="font-serif text-lg text-[#F3E9EC]">{t.patient_display_name}</h3><p className="text-xs text-[#B47A9A] uppercase tracking-widest">{t.country} · {t.treatment_name}</p></div>
              <div className="flex items-center gap-2"><span className={`text-[10px] px-2 py-0.5 rounded-full ${t.published?"bg-[#25D366]/15 text-[#25D366]":"bg-[#B47A9A]/15 text-[#B47A9A]"}`}>{t.published?"Live":"Draft"}</span></div>
            </div>
            <p className="text-sm text-[#F3E9EC]/80 mt-3 italic">"{t.quote}"</p>
            <div className="mt-3 pt-3 border-t border-[#5E3A5C]/30 flex items-center justify-between text-xs text-[#F3E9EC]/50">
              <span>Consent: {t.consent?"Yes":"No"}</span>
              <button onClick={()=>del(t.id)} className="text-red-300 hover:text-red-400 flex items-center gap-1"><Trash2 className="w-3 h-3"/>Delete</button>
            </div>
          </div>
        ))}
        {tests.length===0 && <p className="text-[#F3E9EC]/50">No testimonials yet.</p>}
      </div>
    </div>
  );
}

function ConvDetail({ sessionId }) {
  const [msgs, setMsgs] = useState([]);
  useEffect(() => { http.get(`/admin/conversations/${sessionId}`).then(r => setMsgs(r.data)); }, [sessionId]);
  return (
    <div className="mt-4 space-y-2 max-h-96 overflow-y-auto">
      {msgs.map(m => (
        <div key={m.id} className={`text-sm p-3 rounded-lg ${m.role==="user"?"bg-[#B47A9A]/15 ml-8":"bg-[#5E3A5C]/20 mr-8"}`}>
          <span className="text-[10px] uppercase tracking-widest text-[#B47A9A]">{m.role}</span>
          <p className="text-[#F3E9EC]/90 mt-1 whitespace-pre-wrap">{m.content}</p>
        </div>
      ))}
    </div>
  );
}

// -------------- Entity Admin (Treatments / Hospitals / Doctors) --------------
const TREATMENT_FIELDS = [
  { k: "slug", label: "Slug *", type: "text" },
  { k: "name", label: "Name *", type: "text" },
  { k: "category", label: "Category *", type: "text", hint: "e.g. Cardiology, Orthopaedics" },
  { k: "overview", label: "Overview", type: "textarea" },
  { k: "who_may_need_it", label: "Who may need it", type: "textarea" },
  { k: "procedure_overview", label: "Procedure overview", type: "textarea" },
  { k: "recovery", label: "Recovery", type: "textarea" },
  { k: "hospital_stay", label: "Hospital stay", type: "text" },
];
const HOSPITAL_FIELDS = [
  { k: "slug", label: "Slug *", type: "text" },
  { k: "name", label: "Name *", type: "text" },
  { k: "city", label: "City *", type: "text" },
  { k: "state", label: "State", type: "text" },
  { k: "description", label: "Description", type: "textarea" },
  { k: "specialties", label: "Specialties (comma-separated)", type: "csv" },
  { k: "facilities", label: "Facilities (comma-separated)", type: "csv" },
  { k: "accreditations", label: "Accreditations (comma-separated)", type: "csv" },
  { k: "hero_image", label: "Hero image URL", type: "text" },
];
const DOCTOR_FIELDS = [
  { k: "slug", label: "Slug *", type: "text" },
  { k: "name", label: "Name *", type: "text" },
  { k: "specialty", label: "Specialty *", type: "text" },
  { k: "sub_specialty", label: "Sub-specialty", type: "text" },
  { k: "experience_years", label: "Years of experience", type: "number" },
  { k: "qualifications", label: "Qualifications (comma-separated)", type: "csv" },
  { k: "expertise", label: "Expertise (comma-separated)", type: "csv" },
  { k: "languages", label: "Languages (comma-separated)", type: "csv" },
  { k: "city", label: "City", type: "text" },
  { k: "photo", label: "Photo URL", type: "text" },
];

function useEntity(endpoint) {
  const [items, setItems] = useState([]);
  const refresh = () => http.get(`/admin/${endpoint}`).then(r => setItems(r.data));
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [endpoint]);
  return [items, refresh];
}

function fromDoc(doc, fields) {
  const f = {};
  for (const fd of fields) {
    const v = doc?.[fd.k];
    f[fd.k] = fd.type === "csv" ? (Array.isArray(v) ? v.join(", ") : "") : (v ?? "");
  }
  f.published = doc?.published ?? true;
  return f;
}
function toDoc(form, fields) {
  const out = { published: !!form.published };
  for (const fd of fields) {
    let v = form[fd.k];
    if (fd.type === "csv") v = String(v || "").split(",").map(s=>s.trim()).filter(Boolean);
    if (fd.type === "number") v = v === "" || v == null ? 0 : Number(v);
    out[fd.k] = v;
  }
  return out;
}

function EntityForm({ fields, initial, onCancel, onSubmit, label }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div className="mp-card p-6 mb-6 space-y-3" data-testid={`${label.toLowerCase()}-form`}>
      {fields.map(fd => (
        <div key={fd.k}>
          <label className="text-xs uppercase tracking-widest text-[#F3E9EC]/60 mb-1.5 block">{fd.label}{fd.hint && <span className="ml-2 text-[#F3E9EC]/40 normal-case tracking-normal">— {fd.hint}</span>}</label>
          {fd.type === "textarea"
            ? <textarea className="mp-textarea min-h-[80px]" value={form[fd.k]||""} onChange={e=>set(fd.k, e.target.value)} data-testid={`${label.toLowerCase()}-${fd.k}`}/>
            : <input className="mp-input" type={fd.type === "number" ? "number" : "text"} value={form[fd.k] ?? ""} onChange={e=>set(fd.k, e.target.value)} data-testid={`${label.toLowerCase()}-${fd.k}`}/>}
        </div>
      ))}
      <div className="flex items-center justify-between pt-2">
        <label className="flex items-center gap-2 text-sm text-[#F3E9EC]/75"><input type="checkbox" checked={!!form.published} onChange={e=>set("published", e.target.checked)} className="accent-[#B47A9A]"/>Published</label>
        <div className="flex gap-2">
          <button onClick={onCancel} className="btn-outline-mauve px-4 py-2 text-sm">Cancel</button>
          <button onClick={() => onSubmit(form)} className="btn-primary px-5 py-2 text-sm" data-testid={`${label.toLowerCase()}-save`}>Save</button>
        </div>
      </div>
    </div>
  );
}

function EntityAdmin({ endpoint, fields, label }) {
  const [items, refresh] = useEntity(endpoint);
  const [editingSlug, setEditingSlug] = useState(null);
  const [creating, setCreating] = useState(false);
  const create = async (form) => {
    try {
      await http.post(`/admin/${endpoint}`, toDoc(form, fields));
      setCreating(false); refresh();
    } catch(e){ alert(e.response?.data?.detail || "Failed"); }
  };
  const update = async (key, form) => {
    try {
      await http.patch(`/admin/${endpoint}/${key}`, toDoc(form, fields));
      setEditingSlug(null); refresh();
    } catch(e){ alert("Failed"); }
  };
  const del = async (key) => {
    if (!window.confirm(`Delete "${key}"?`)) return;
    await http.delete(`/admin/${endpoint}/${key}`);
    refresh();
  };
  const toggle = async (item) => {
    await http.patch(`/admin/${endpoint}/${item.slug}`, { published: !item.published });
    refresh();
  };
  return (
    <div data-testid={`admin-${endpoint}-panel`}>
      <div className="mb-4 flex justify-end">
        <button onClick={()=>{ setCreating(!creating); setEditingSlug(null); }} className="btn-primary px-4 py-2 flex items-center gap-2 text-sm" data-testid={`admin-new-${endpoint}`}><Plus className="w-4 h-4"/>{creating?"Cancel":`New ${label}`}</button>
      </div>
      {creating && (
        <EntityForm fields={fields} label={label}
          initial={fromDoc({}, fields)}
          onCancel={()=>setCreating(false)}
          onSubmit={create}/>
      )}
      <div className="grid md:grid-cols-2 gap-3">
        {items.map(it => (
          <div key={it.id} className="mp-card p-5" data-testid={`admin-${endpoint}-row-${it.slug}`}>
            {editingSlug === it.slug ? (
              <EntityForm fields={fields} label={label}
                initial={fromDoc(it, fields)}
                onCancel={()=>setEditingSlug(null)}
                onSubmit={(f)=>update(it.slug, f)}/>
            ) : (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2"><h3 className="font-serif text-lg text-[#F3E9EC] truncate">{it.name}</h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ${it.published?"bg-[#25D366]/15 text-[#25D366]":"bg-[#B47A9A]/15 text-[#B47A9A]"}`}>{it.published?"Live":"Draft"}</span>
                  </div>
                  <p className="text-xs text-[#F3E9EC]/50 mt-1 font-mono truncate">/{endpoint}/{it.slug}</p>
                  <p className="text-xs text-[#B47A9A] mt-1">{it.category || it.specialty || it.city}</p>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button onClick={()=>toggle(it)} className="btn-outline-mauve px-2 py-1 text-xs" title={it.published?"Unpublish":"Publish"}>{it.published?<EyeOff className="w-3.5 h-3.5"/>:<Eye className="w-3.5 h-3.5"/>}</button>
                  <button onClick={()=>{ setEditingSlug(it.slug); setCreating(false); }} className="btn-outline-mauve px-2 py-1 text-xs" data-testid={`admin-${endpoint}-edit-${it.slug}`}><Edit2 className="w-3.5 h-3.5"/></button>
                  <button onClick={()=>del(it.slug)} className="btn-outline-mauve px-2 py-1 text-xs text-red-300" data-testid={`admin-${endpoint}-delete-${it.slug}`}><Trash2 className="w-3.5 h-3.5"/></button>
                </div>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <p className="text-[#F3E9EC]/50">No {label.toLowerCase()}s yet.</p>}
      </div>
    </div>
  );
}

// -------------- Country Section Builder --------------
const SECTION_OPTIONS = [
  { key: "hero", label: "Hero" },
  { key: "how_it_works", label: "How it works" },
  { key: "featured_treatments", label: "Featured treatments" },
  { key: "cost_savings", label: "Cost savings / calculator" },
  { key: "travel_visa", label: "Travel & Visa" },
  { key: "faqs", label: "FAQs" },
  { key: "cta", label: "Final CTA" },
];

function CountryAdmin() {
  const [countries, setCountries] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [selected, setSelected] = useState(null);
  const refresh = () => http.get("/admin/countries").then(r => setCountries(r.data));
  useEffect(() => {
    refresh();
    http.get("/treatments").then(r => setTreatments(r.data));
  }, []);
  return (
    <div className="grid md:grid-cols-[280px_1fr] gap-6" data-testid="admin-countries-panel">
      <div className="mp-card p-4 h-fit">
        <div className="text-xs uppercase tracking-widest text-[#F3E9EC]/60 mb-3">All countries</div>
        <div className="space-y-1">
          {countries.map(c => (
            <button key={c.id} onClick={()=>setSelected(c.slug)} className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm ${selected===c.slug?"bg-[#B47A9A]/15 text-white":"text-[#F3E9EC]/75 hover:bg-[#5E3A5C]/25"}`} data-testid={`admin-country-select-${c.slug}`}>
              <span>{c.flag}</span><span className="flex-1">{c.name}</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${c.published?"bg-[#25D366]/15 text-[#25D366]":"bg-[#B47A9A]/15 text-[#B47A9A]"}`}>{c.published?"Live":"Draft"}</span>
            </button>
          ))}
        </div>
      </div>
      {selected ? <CountryEditor slug={selected} countries={countries} treatments={treatments} onSaved={refresh}/>
                : <div className="mp-card p-10 text-center text-[#F3E9EC]/50">Select a country to edit its content and sections.</div>}
    </div>
  );
}

function CountryEditor({ slug, countries, treatments, onSaved }) {
  const initial = countries.find(c => c.slug === slug);
  const [c, setC] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  useEffect(() => { setC(countries.find(x => x.slug === slug)); }, [slug, countries]);
  if (!c) return null;
  const set = (k, v) => setC(x => ({ ...x, [k]: v }));
  const setSection = (i, patch) => setC(x => ({ ...x, sections: x.sections.map((s, j) => j===i ? { ...s, ...patch } : s) }));
  const moveSection = (i, dir) => setC(x => {
    const arr = [...x.sections].sort((a,b)=>a.order-b.order);
    const j = i + dir;
    if (j < 0 || j >= arr.length) return x;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...x, sections: arr.map((s, idx) => ({ ...s, order: idx + 1 })) };
  });
  const addSection = (key) => {
    if (c.sections.some(s => s.key === key)) return alert("Section already present");
    const next = { key, visible: true, order: (c.sections.length ? Math.max(...c.sections.map(s=>s.order)) : 0) + 1 };
    setC(x => ({ ...x, sections: [...x.sections, next] }));
  };
  const removeSection = (key) => setC(x => ({ ...x, sections: x.sections.filter(s => s.key !== key).map((s, i)=>({...s, order:i+1})) }));
  const addFaq = () => setC(x => ({ ...x, faqs: [...(x.faqs||[]), { q: "", a: "" }] }));
  const updateFaq = (i, patch) => setC(x => ({ ...x, faqs: x.faqs.map((f, j) => j===i ? { ...f, ...patch } : f) }));
  const removeFaq = (i) => setC(x => ({ ...x, faqs: x.faqs.filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      // recalc treatment_ids from featured_treatments slugs
      const tx_ids = (c.featured_treatments || []).map(sg => (treatments.find(t => t.slug === sg) || {}).id).filter(Boolean);
      const payload = { ...c, treatment_ids: tx_ids };
      delete payload._id;
      await http.patch(`/admin/countries/${c.slug}`, payload);
      setSavedAt(new Date());
      onSaved();
    } catch(e){ alert("Save failed"); }
    finally { setSaving(false); }
  };

  const sortedSections = [...(c.sections || [])].sort((a,b)=>a.order-b.order);
  const availableToAdd = SECTION_OPTIONS.filter(o => !c.sections.some(s => s.key === o.key));

  return (
    <div className="space-y-6" data-testid={`country-editor-${c.slug}`}>
      <div className="flex items-center justify-between mp-card p-5">
        <div>
          <div className="flex items-center gap-3"><span className="text-2xl">{c.flag}</span><h3 className="font-serif text-2xl text-[#F3E9EC]">{c.name}</h3></div>
          <p className="text-xs text-[#F3E9EC]/50 mt-1 font-mono">/countries/{c.slug} · currency {c.currency}</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-[#F3E9EC]/75"><input type="checkbox" checked={!!c.published} onChange={e=>set("published", e.target.checked)} className="accent-[#B47A9A]"/>Published</label>
          <button onClick={save} disabled={saving} className="btn-primary px-5 py-2.5 text-sm flex items-center gap-2" data-testid="country-save"><Save className="w-4 h-4"/>{saving?"Saving…":"Save changes"}</button>
        </div>
      </div>
      {savedAt && <p className="text-xs text-[#25D366]">Saved at {savedAt.toLocaleTimeString()}</p>}

      <div className="mp-card p-6 space-y-3">
        <div className="overline">Hero content</div>
        <input className="mp-input" placeholder="Hero title" value={c.hero_title||""} onChange={e=>set("hero_title", e.target.value)}/>
        <textarea className="mp-textarea" placeholder="Hero subtitle" value={c.hero_subtitle||""} onChange={e=>set("hero_subtitle", e.target.value)}/>
        <input className="mp-input" placeholder="Hero image URL" value={c.hero_image||""} onChange={e=>set("hero_image", e.target.value)}/>
        <div className="grid md:grid-cols-2 gap-3">
          <input className="mp-input" placeholder="Flight duration (e.g. 8–10 hours direct)" value={c.flight_hours||""} onChange={e=>set("flight_hours", e.target.value)}/>
          <input className="mp-input" placeholder="Currency code (e.g. GBP)" value={c.currency||""} onChange={e=>set("currency", e.target.value.toUpperCase())}/>
        </div>
        <textarea className="mp-textarea" placeholder="Visa information" value={c.visa_info||""} onChange={e=>set("visa_info", e.target.value)}/>
      </div>

      <div className="mp-card p-6">
        <div className="overline mb-3">Sections (drag order using buttons)</div>
        <div className="space-y-2">
          {sortedSections.map((s, i) => (
            <div key={s.key} className="flex items-center gap-2 p-3 rounded-lg bg-[#5E3A5C]/15 border border-[#5E3A5C]/30" data-testid={`section-row-${s.key}`}>
              <span className="font-mono text-xs text-[#B47A9A] w-8">{String(i+1).padStart(2,"0")}</span>
              <span className="flex-1 text-sm text-[#F3E9EC]">{(SECTION_OPTIONS.find(o=>o.key===s.key)||{}).label || s.key}</span>
              <button onClick={()=>moveSection(i, -1)} disabled={i===0} className="text-[#F3E9EC]/60 hover:text-white disabled:opacity-30 p-1" data-testid={`section-up-${s.key}`}><ChevronUp className="w-4 h-4"/></button>
              <button onClick={()=>moveSection(i, 1)} disabled={i===sortedSections.length-1} className="text-[#F3E9EC]/60 hover:text-white disabled:opacity-30 p-1" data-testid={`section-down-${s.key}`}><ChevronDown className="w-4 h-4"/></button>
              <button onClick={()=>setSection(sortedSections.indexOf(s), { visible: !s.visible })} className={`px-2 py-1 rounded text-xs ${s.visible?"text-[#25D366]":"text-[#F3E9EC]/40"}`} data-testid={`section-toggle-${s.key}`}>{s.visible?<Eye className="w-3.5 h-3.5"/>:<EyeOff className="w-3.5 h-3.5"/>}</button>
              <button onClick={()=>removeSection(s.key)} className="text-red-300 hover:text-red-400 p-1" data-testid={`section-remove-${s.key}`}><Trash2 className="w-3.5 h-3.5"/></button>
            </div>
          ))}
        </div>
        {availableToAdd.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2 pt-3 border-t border-[#5E3A5C]/30">
            <span className="text-xs text-[#F3E9EC]/50 uppercase tracking-widest">Add section:</span>
            {availableToAdd.map(o => (
              <button key={o.key} onClick={()=>addSection(o.key)} className="text-xs px-3 py-1 rounded-full border border-[#B47A9A]/40 text-[#B47A9A] hover:bg-[#B47A9A]/15" data-testid={`section-add-${o.key}`}>+ {o.label}</button>
            ))}
          </div>
        )}
      </div>

      <div className="mp-card p-6">
        <div className="overline mb-3">Featured treatments (slugs)</div>
        <div className="flex flex-wrap gap-2">
          {treatments.map(t => {
            const selected = (c.featured_treatments||[]).includes(t.slug);
            return (
              <button key={t.id} onClick={()=>set("featured_treatments", selected ? c.featured_treatments.filter(x=>x!==t.slug) : [...(c.featured_treatments||[]), t.slug])} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${selected?"bg-[#B47A9A] border-[#B47A9A] text-[#1A0F1B]":"border-[#5E3A5C]/40 text-[#F3E9EC]/70 hover:border-[#B47A9A]/60"}`} data-testid={`ft-toggle-${t.slug}`}>{t.name}</button>
            );
          })}
        </div>
      </div>

      <div className="mp-card p-6">
        <div className="flex items-center justify-between mb-3"><div className="overline">FAQs</div><button onClick={addFaq} className="text-xs px-3 py-1 rounded-full border border-[#B47A9A]/40 text-[#B47A9A]" data-testid="country-add-faq">+ Add FAQ</button></div>
        <div className="space-y-3">
          {(c.faqs||[]).map((f, i) => (
            <div key={i} className="p-3 rounded-lg bg-[#5E3A5C]/15 border border-[#5E3A5C]/30 space-y-2" data-testid={`country-faq-${i}`}>
              <input className="mp-input" placeholder="Question" value={f.q} onChange={e=>updateFaq(i, { q: e.target.value })}/>
              <textarea className="mp-textarea" placeholder="Answer" value={f.a} onChange={e=>updateFaq(i, { a: e.target.value })}/>
              <button onClick={()=>removeFaq(i)} className="text-xs text-red-300 hover:text-red-400 flex items-center gap-1"><Trash2 className="w-3 h-3"/>Remove FAQ</button>
            </div>
          ))}
          {(c.faqs||[]).length === 0 && <p className="text-xs text-[#F3E9EC]/50">No FAQs yet.</p>}
        </div>
      </div>
    </div>
  );
}

